import { AttributeType } from "@interfaces/attribute.interface";
import {
  formatNumberWithCommas,
  getCurrencyDisplayValue,
  getCurrencySuffix,
  sanitizeCurrencyInput,
} from "@utils/currency.util";

import { MASK_INPUT_VARIANTS, MaskInputVariant } from "./MaskInput.config";

const DECIMALS = 2;

/**
 * Credit pricing accepts an optional L+/S+ prefix and up to 999.99 — ported from oberon's
 * `isValidCreditPricingValue`, since credit is the one masked type that isn't purely numeric.
 */
export function isValidCreditPricingValue(value: string): boolean {
  if (value === "") return true;
  if (!/^(?:L\+|S\+)?(?!\.)\d{0,3}(?:\.\d{0,2})?$|^[LS]\+?$/.test(value)) return false;

  const numericPart = value.replace(/^(L\+|S\+)?/, "");
  const numeric = parseFloat(numericPart);

  return !numericPart || isNaN(numeric) || numeric <= 999.99;
}

/**
 * Keystroke filter driven by the variant: strips what can't belong to the value, applies the
 * decimal and sign rules, and returns null when `enforceMinMax` rejects the result so the caller
 * can drop the change instead of surfacing an error.
 */
export function sanitizeMaskInputValue(value: string, variant: MaskInputVariant): string | null {
  const config = MASK_INPUT_VARIANTS[variant];

  if (variant === AttributeType.CREDIT) {
    const credit = value.toUpperCase();

    return isValidCreditPricingValue(credit) ? credit : null;
  }

  const allowNegative = config.min === undefined || config.min < 0;
  const sanitized = sanitizeCurrencyInput(value, config.integer ? 0 : DECIMALS);
  const next = allowNegative ? sanitized : sanitized.replace("-", "");

  if (!config.enforceMinMax) return next;

  const numeric = Number(next);

  if (isNaN(numeric)) return next;
  if (config.min !== undefined && numeric < config.min) return null;
  if (config.max !== undefined && numeric > config.max) return null;

  return next;
}

/** Grouped digits while editing; the read-only form — scaled or fixed-decimal — when it isn't. */
export function getMaskInputDisplayValue(
  value: string,
  variant: MaskInputVariant,
  isFocused: boolean
): string {
  const config = MASK_INPUT_VARIANTS[variant];

  if (config.disableCommas) return value;
  if (isFocused || !value) return formatNumberWithCommas(value);
  if (config.scaleWhenReadOnly) return getCurrencyDisplayValue(value);

  if (config.displayDecimals !== undefined) {
    const numeric = Number(value);

    if (!isNaN(numeric)) {
      return numeric.toLocaleString("en-US", {
        minimumFractionDigits: config.displayDecimals,
        maximumFractionDigits: config.displayDecimals,
      });
    }
  }

  return formatNumberWithCommas(value);
}

/** Trailing unit: the variant's own, or the currency scale once the field is read-only. */
export function getMaskInputSuffix(
  value: string,
  variant: MaskInputVariant,
  isFocused: boolean
): string {
  const config = MASK_INPUT_VARIANTS[variant];

  if (!config.scaleWhenReadOnly) return config.prefixes.final ?? "";

  return isFocused ? "" : getCurrencySuffix(value);
}
